import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Pause, Play } from "lucide-react";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";
import { Tabs } from "@shared/ui/tabs";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import {
  useChannelTemplates,
  useChannelType,
  useSendCommunication,
} from "@features/cx/consumerManagement/hooks";
import {
  useAcknowledgeBulkRequest,
  useBulkDisconnectDetails,
  useRejectBulkRequest,
  useBulkRequestConsumersDownload,
  useBulkRequestConsumersList,
  useHoldBulkRequest,
  useResumeBulkRequest,
} from "@features/cx/disconnect/hooks";
import { queryClient } from "@shared/api/queries/queryClient";

import { BulkReconnectionDetailCard } from "../detailCard/BulkReconnectionDetailCard";
import { QuickActions } from "../quickActions/QuickActions";
import ConsumersTab from "./tabs/consumers/consumers";
import PremisePlanTab from "./tabs/premisePlan/premisePlan";
import MeterActivationTab from "./tabs/meterActivation/meterActivation";
import { TIMELINE_LIMIT } from "@shared/utils/constants";

interface ActionButton {
  id: string;
  label: string;
  icon: any;
  variant:
    | "default"
    | "outline"
    | "secondary"
    | "destructive"
    | "ghost"
    | "link";
  onClick: () => void;
  condition: (status: string) => boolean;
  isLoading?: boolean;
}

const BulkReconnectionLanding = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const [currentStatus, setCurrentStatus] = useState("pending");
  const [isNotifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // API Data & Mutations
  const { data: requestDetails } = useBulkDisconnectDetails(id || "");
  const { mutateAsync: acknowledgeRequest, isPending: isAcknowledging } =
    useAcknowledgeBulkRequest(id || "");
  const { mutateAsync: rejectRequest } = useRejectBulkRequest(id || "");
  const { mutateAsync: holdRequest, isPending: isHolding } = useHoldBulkRequest(
    id || "",
  );
  const { mutateAsync: resumeRequest, isPending: isResuming } =
    useResumeBulkRequest(id || "");
  const { download: downloadConsumers, isDownloading: isExporting } =
    useBulkRequestConsumersDownload(id || "");

  const { data: consumersListResponse } = useBulkRequestConsumersList({
    batchId: id || "",
    remoteUtilityId: Number(remoteUtilityId),
    page: 1,
    limit: 100,
  });

  const emails =
    consumersListResponse?.results
      ?.map((c: any) => c.email)
      .filter(Boolean)
      .join(",") || "";
  const phones =
    consumersListResponse?.results
      ?.map((c: any) => c.contactNo)
      .filter(Boolean)
      .join(",") || "";

  // Sync status from API
  useEffect(() => {
    if (requestDetails?.statusDisplay) {
      setCurrentStatus(requestDetails.statusDisplay.toLowerCase());
    }
  }, [requestDetails]);

  // Communication Handlers
  const { mutateAsync: sendCommunication } = useSendCommunication(id || "");
  const createCommunicationWrapper = (consumerId: string) => ({
    mutateAsync: (data: any) => sendCommunication(data),
  });

  const handleAcknowledge = async () => {
    try {
      await acknowledgeRequest();
      setCurrentStatus("in progress");
      queryClient.invalidateQueries({ queryKey: ["cx", "bulk-requests", id] });
    } catch (error) {
      // Error handled by hook toast
    }
  };

  const handlePutOnHold = async () => {
    try {
      await holdRequest();
      setCurrentStatus("on hold");
      queryClient.invalidateQueries({ queryKey: ["cx", "bulk-requests", id] });
    } catch (error) {
      // Error handled by hook toast
    }
  };

  const handleResume = async () => {
    try {
      await resumeRequest();
      setCurrentStatus("in progress");
      queryClient.invalidateQueries({ queryKey: ["cx", "bulk-requests", id] });
    } catch (error) {
      // Error handled by hook toast
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await rejectRequest({ rejection_reason: reason });
      setCurrentStatus("rejected");
      setRejectDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["cx", "bulk-requests", id] });
    } catch (error) {
      // Error handled by hook toast
    } finally {
      setIsRejecting(false);
    }
  };

  const handleExport = () => {
    downloadConsumers();
  };

  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      isLoading: isAcknowledging,
      condition: (status) =>
        status === "pending" ||
        status === "pending approval" ||
        requestDetails?.statusDisplay?.toLowerCase() === "pending approval",
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      isLoading: isHolding,
      condition: (status) =>
        status === "acknowledged" ||
        status === "in progress" ||
        requestDetails?.statusDisplay?.toLowerCase() === "acknowledged" ||
        requestDetails?.statusDisplay?.toLowerCase() === "in progress",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      isLoading: isResuming,
      condition: (status) =>
        status === "on hold" ||
        status === "hold" ||
        requestDetails?.statusDisplay?.toLowerCase() === "hold" ||
        requestDetails?.statusDisplay?.toLowerCase() === "on hold",
    },
  ];

  const request = {
    id: id || "1",
    requestNo: requestDetails?.batchNo || "Loading...",
    reason: requestDetails?.requestType || "Service Reactivation",
    status: currentStatus,
    preferredDate: requestDetails?.preferredDate || "NA",
    timeSlot: requestDetails?.preferredTimeSlotDisplay || "NA",
    notes: requestDetails?.additionalNotes || "",
    auditInfo: {
      createdBy: requestDetails?.audit?.createdBy || "NA",
      createdOn: requestDetails?.audit?.createdOn || "NA",
      approvedBy: requestDetails?.audit?.approvedBy || "NA",
      approvedOn: requestDetails?.audit?.approvedOn || "NA",
    },
    stats: {
      total: requestDetails?.kpis?.totalConsumers || 0,
      completed: requestDetails?.kpis?.completedConsumers || 0,
      pending: requestDetails?.kpis?.pendingConsumers || 0,
      createdOn: requestDetails?.kpis?.createdOn || "NA",
    },
  };

  const tabComponents = {
    consumers: {
      label: `Consumers`,
      component: <ConsumersTab />,
      count: request.stats.total,
    },
    premisePlan: { label: "Premise & Plan", component: <PremisePlanTab /> },
    meterActivation: {
      label: "Meter & Activation",
      component: <MeterActivationTab />,
    },
    notes: {
      label: "Notes",
      component: (
        <NotesTab
          requestId={id}
          remoteUtilityId={Number(remoteUtilityId)}
          source="bulk-reconnect"
          createdBy={request?.auditInfo?.createdBy}
        />
      ),
    },
    timeline: {
      label: "Time Line",
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId)}
          entityId={id}
          actionType="bulk_request"
        />
      ),
    },
  };

  const urlMapping = {
    consumers: "consumers",
    premisePlan: "premisePlan",
    meterActivation: "meterActivation",
    notes: "notes",
    timeline: "timeline",
  };

  return (
    <div className="space-y-6">
      {/* Custom Header Matching Bulk Disconnect */}
      <div className="flex items-center justify-between">
        <div className="mb-4 flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700 hover:text-indigo-600 transition-colors"
            onClick={() => navigate("/cx/reconnections")}
          />
          <div className="flex flex-col">
            <PageHeader
              title="View Bulk Reconnection"
              subtitle="Manage and monitor bulk consumer reconnection progress"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {actionButtons
            .filter((btn) => btn.condition(currentStatus))
            .map((btn) => (
              <Button
                key={btn.id}
                variant={btn.variant}
                className={
                  btn.variant === "default"
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white px-6 font-semibold shadow-sm"
                    : ""
                }
                onClick={btn.onClick}
                disabled={btn.isLoading}
              >
                <btn.icon className="h-4 w-4 mr-2" />
                {btn.isLoading ? `${btn.label}ing...` : btn.label}
              </Button>
            ))}
        </div>
      </div>

      {/* Main Grid Matching Bulk Disconnect (9/12 and 3/12) */}
      <div className="grid grid-cols-12 gap-6 items-start">
        <div className="col-span-9 flex flex-col gap-6">
          <BulkReconnectionDetailCard
            request={request}
            currentStatus={currentStatus}
          />

          {/* Tabs */}
          <Tabs
            defaultValue="consumers"
            className="w-full"
            tabsListClassName="grid grid-cols-5 w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm"
            urlMapping={urlMapping}
            tabComponents={tabComponents}
            level={0}
          />
        </div>

        <div className="col-span-3 flex flex-col gap-4">
          <QuickActions
            consumerCount={request.stats.total}
            onNotify={() => setNotifyDialogOpen(true)}
            onExport={handleExport}
            onReject={() => setRejectDialogOpen(true)}
            onAddNote={() => {}}
            isExporting={isExporting}
            isRejecting={isRejecting}
            currentStatus={currentStatus}
          />
          {/* Timeline Widget - Exactly like Bulk Disconnect */}
          <TimelineTab
            remoteUtilityId={Number(remoteUtilityId)}
            entityId={request?.id.toString()}
            actionType="bulk_request"
            limit={TIMELINE_LIMIT}
          />
        </div>
      </div>

      {/* Modals */}
      <CommunicationDialog
        isOpen={isNotifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        consumerId={id || ""}
        email={emails}
        phone={phones}
        remoteUtilityId={Number(remoteUtilityId)}
        communicationType="bulk_reconnection"
        consumerCount={request.stats.total}
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={createCommunicationWrapper}
      />

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Reconnection Request"
        description="Please provide a reason for rejecting this bulk reconnection."
        label="Rejection Reason"
        placeholder="Reason for rejection..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
};

export default BulkReconnectionLanding;
